import React, { useEffect, useState, useMemo } from 'react';
import { clipTopicName } from '../../utils/topicUtils';
import './OverviewPanel.css';

/**
 * OverviewPanel — filter-aware dashboard overview tab.
 *
 * Mixes chart types to avoid a wall-of-text feel:
 *   - KPI tiles
 *   - Themes coverage  → donut chart + legend
 *   - Industry coverage → treemap (squarified)
 *   - Top topics       → compact enriched list (chips carry meaning)
 *   - Unclaimed topics → scatter (volume × themed%)
 *   - Top documents    → slim list
 *   - Concept cloud    → wordcloud
 */
function OverviewPanel({
  apiBaseUrl,
  authFetch,
  filters,
  topicView,
  schemaConfig,
  onTopicClick,
  onIndustryClick,
  onThemeClick,
  onDocClick,
  onHelpSection,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    setError(null);
    const body = { filters: filters || {}, view: topicView || undefined };
    authFetch(`${apiBaseUrl}/api/overview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('overview fetch failed'))))
      .then((d) => { if (!aborted) setData(d); })
      .catch((err) => { if (!aborted) setError(err.message || String(err)); })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [apiBaseUrl, authFetch, filters, topicView]);

  const labels = schemaConfig?.labels || {};
  const lbl = useMemo(
    () => ({
      industry: labels.industry || 'Industry',
      topic: labels.topic || 'Topic',
      themes: labels.themes || 'Themes',
      record: labels.record || 'Record',
      document: labels.document || 'Document',
      time: labels.time || 'Time',
    }),
    [labels]
  );

  if (loading && !data) {
    return <div className="overview-panel overview-loading">Loading overview…</div>;
  }
  if (error && !data) {
    return <div className="overview-panel overview-error">Could not load overview: {error}</div>;
  }
  if (!data) return <div className="overview-panel">No data.</div>;

  const k = data.kpis || {};
  const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '—');
  const pct = (s) => `${(s * 100).toFixed(s >= 0.1 ? 0 : 1)}%`;
  // Pluralise a label without breaking already-plural ("Topics") or -y words
  // ("Industry" → "Industries"). Avoids artefacts like "topicss".
  const plural = (w) => {
    if (!w) return w;
    if (/s$/i.test(w)) return w;
    if (/[^aeiou]y$/i.test(w)) return `${w.slice(0, -1)}ies`;
    return `${w}s`;
  };

  const yearStr = (k.yearMin && k.yearMax)
    ? (k.yearMin === k.yearMax ? `${k.yearMin}` : `${k.yearMin}–${k.yearMax}`)
    : '—';

  const activeFilterCount = Object.entries(filters || {}).reduce((acc, [key, val]) => {
    if (key === 'unspecifiedMode') return acc;
    if (Array.isArray(val) && val.length > 0) return acc + 1;
    return acc;
  }, 0);

  const maxTopic = (data.topTopics || []).reduce((m, r) => Math.max(m, r.count), 0) || 1;

  // ── Wordcloud sizing ──
  // Tighter range so it reads as one block, not scattered text.
  // Sqrt scale spreads the long tail (Zipf-like weights) more evenly.
  const kws = data.themeKeywords || [];
  const maxW = kws.reduce((m, r) => Math.max(m, r.weight), 0) || 1;
  const minW = kws.reduce((m, r) => Math.min(m, r.weight), maxW);
  const range = Math.max(1, maxW - minW);
  const fontFor = (w) => {
    const t = Math.sqrt(Math.max(0, (w - minW)) / range);
    return Math.round(11 + t * 13); // 11–24px
  };

  return (
    <div className="overview-panel">
      {/* ── Header KPI strip ── */}
      <div className="overview-kpi-row">
        {onHelpSection && (
          <button
            type="button"
            className="overview-help-btn"
            title="About the Overview tab"
            aria-label="About the Overview tab"
            onClick={() => onHelpSection('overviewTab')}
          >
            ⓘ
          </button>
        )}
        <Kpi value={fmt(k.records)} label={k.records === 1 ? (k.recordLabel || lbl.record) : plural(k.recordLabel || lbl.record)} />
        {k.documents != null && <Kpi value={fmt(k.documents)} label={plural(lbl.document).toLowerCase()} />}
        {k.projects != null && <Kpi value={fmt(k.projects)} label="projects" />}
        <Kpi value={fmt(k.industries)} label={plural(lbl.industry)} />
        <Kpi value={fmt(k.topics)} label={lbl.topic} />
        {(k.themes || 0) > 0 && <Kpi value={fmt(k.themes)} label={lbl.themes} />}
        <Kpi value={yearStr} label={`${lbl.time} span`} />
        <Kpi value={activeFilterCount} label="active filters" active={activeFilterCount > 0} />
      </div>

      <div className="overview-grid">
        {/* Row 1: high-signal visuals — donut + treemap side by side */}
        {(data.themeCoverage || []).length > 0 && (
          <section className="overview-card">
            <h3 className="overview-card-title">{lbl.themes} coverage</h3>
            <p className="overview-card-sub">
              Share of {plural(lbl.record).toLowerCase()} per theme — totals can exceed 100% because a {lbl.record.toLowerCase()} may be tagged with multiple themes. The ×N in the legend counts how many {plural(lbl.topic).toLowerCase()} cross-cut that theme.
            </p>
            <DonutChart
              items={data.themeCoverage}
              onSelect={onThemeClick}
              fmt={fmt}
              pct={pct}
              spreadLabel={lbl.topic.toLowerCase()}
            />
          </section>
        )}

        {(data.industryCoverage || []).length > 0 && (
          <section className="overview-card">
            <h3 className="overview-card-title">{lbl.industry} mix</h3>
            <p className="overview-card-sub">
              Each tile represents one {lbl.industry.toLowerCase()} — size reflects how many {lbl.record.toLowerCase()}s belong to it. Click a tile to filter by that {lbl.industry.toLowerCase()}.
            </p>
            <Treemap
              items={data.industryCoverage}
              onSelect={onIndustryClick}
              fmt={fmt}
              pct={pct}
              recordLabel={lbl.record.toLowerCase()}
            />
          </section>
        )}

        {/* Row 2: text-dense lists — top topics + top docs */}
        {(data.topTopics || []).length > 0 && (
          <section className="overview-card">
            <h3 className="overview-card-title">Top {lbl.topic}</h3>
            <p className="overview-card-sub">Largest by volume. Blue chip: dominant {lbl.industry.toLowerCase()}. Orange chips: themes touched.</p>
            <ul className="overview-topic-list">
              {data.topTopics.map((t) => (
                <li key={t.label}>
                  <button
                    type="button"
                    className="overview-topic-row"
                    onClick={() => onTopicClick && onTopicClick(t.label)}
                    title={`Click to filter by ${clipTopicName(t.label)}`}
                  >
                    <div className="overview-topic-head">
                      <span className="overview-topic-name">{clipTopicName(t.label)}</span>
                      <span className="overview-topic-count">{fmt(t.count)}</span>
                    </div>
                    <div className="overview-topic-meta">
                      {t.dominantIndustry && (
                        <span className="overview-chip overview-chip-cluster" title={`${pct(t.industryShare)} of this ${lbl.topic.toLowerCase()}`}>
                          {clipTopicName(t.dominantIndustry)} · {pct(t.industryShare)}
                        </span>
                      )}
                      {(t.themes || []).map((th) => (
                        <span key={th} className="overview-chip overview-chip-theme">{th}</span>
                      ))}
                    </div>
                    <div className="overview-topic-bar">
                      <div className="overview-topic-bar-fill" style={{ width: `${(t.count / maxTopic) * 100}%` }} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {(data.topDocuments || []).length > 0 && (
          <section className="overview-card">
            <h3 className="overview-card-title">Top {lbl.document.toLowerCase()}s</h3>
            <p className="overview-card-sub">{lbl.document}s contributing the most {lbl.record.toLowerCase()}s to the current slice.</p>
            <ul className="overview-doc-list">
              {data.topDocuments.map((d) => (
                <li key={d.filename}>
                  <button
                    type="button"
                    className="overview-doc-row"
                    onClick={() => onDocClick && onDocClick({ filename: d.filename, paraIdx: null })}
                    title={`Open ${d.filename}`}
                  >
                    <div className="overview-doc-head">
                      <span className="overview-doc-name">{d.filename}</span>
                      <span className="overview-doc-count">{fmt(d.count)} {lbl.record.toLowerCase()}s</span>
                    </div>
                    <div className="overview-doc-meta">
                      {d.project && <span className="overview-chip">project {d.project}</span>}
                      {d.industry && <span className="overview-chip overview-chip-cluster">{d.industry}</span>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Row 3: opportunity scatter (full width) */}
        {(data.unclaimedTopics || []).length > 0 && (
          <section className="overview-card overview-card-wide">
            <h3 className="overview-card-title">Unclaimed {lbl.topic} — opportunity map</h3>
            <p className="overview-card-sub">
              Volume (x) vs theme coverage (y). The warm zone toward the bottom-right is a soft hint, not a hard cutoff — points drifting that way are worth a closer look as candidates for new {lbl.themes.toLowerCase()}.
            </p>
            <OpportunityScatter
              items={data.unclaimedTopics}
              onSelect={onTopicClick}
              fmt={fmt}
              pct={pct}
              recordLabel={lbl.record.toLowerCase()}
            />
          </section>
        )}

        {/* Row 4: concept cloud (full width, compact) */}
        {kws.length > 0 && (
          <section className="overview-card overview-card-wide">
            <h3 className="overview-card-title">Concept cloud</h3>
            <p className="overview-card-sub">Frequent terms across {lbl.topic.toLowerCase()} labels in the current slice.</p>
            <div className="overview-wordcloud">
              {kws.map((w) => (
                <span
                  key={w.word}
                  className="overview-word"
                  style={{ fontSize: `${fontFor(w.weight)}px` }}
                  title={`${w.word} — weight ${fmt(w.weight)}`}
                >
                  {w.word}
                </span>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

function Kpi({ value, label, active }) {
  return (
    <div className={`overview-kpi${active ? ' overview-kpi-filters active' : ''}`}>
      <div className="overview-kpi-value">{value}</div>
      <div className="overview-kpi-label">{label}</div>
    </div>
  );
}

// Themed palette — mid-saturation tones readable on both light & dark cards.
// Detect dark theme at render time so contrast holds in both modes.
const PALETTE_LIGHT = [
  '#1c566b', '#d4a04a', '#5a8d9f', '#8b5a1a', '#2d7a8c',
  '#a87832', '#3f6f7e', '#c08b3a', '#71a4b3', '#7a6d5a',
  '#4a8294', '#b58647', '#356676', '#9d6a28', '#5e9bab',
  '#876544', '#467d8e', '#cf9856', '#264f5d', '#8a703b',
];
const PALETTE_DARK = [
  '#5cb8cc', '#e8a04a', '#7dcad8', '#d68c35', '#4a99ab',
  '#c4b85e', '#88c5d4', '#f0be78', '#5fa9bc', '#a4986a',
  '#6db8c9', '#d99f5c', '#4a9aac', '#bf8a48', '#7ccad9',
  '#b09d6c', '#5db1c4', '#e6a866', '#3f8c9e', '#a78f5e',
];
function isDarkTheme() {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('theme-dark')
      || document.body.classList.contains('theme-dark');
}
const colorAt = (i) => (isDarkTheme() ? PALETTE_DARK : PALETTE_LIGHT)[i % PALETTE_LIGHT.length];

// ── Donut chart ───────────────────────────────────────────────────────
function DonutChart({ items, onSelect, fmt, pct, spreadLabel }) {
  const total = items.reduce((s, it) => s + it.count, 0) || 1;
  const size = 180;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = 78;
  const rInner = 48;

  let cumulative = 0;
  const arcs = items.map((it, i) => {
    const startAngle = (cumulative / total) * Math.PI * 2 - Math.PI / 2;
    cumulative += it.count;
    const endAngle = (cumulative / total) * Math.PI * 2 - Math.PI / 2;
    const large = endAngle - startAngle > Math.PI ? 1 : 0;
    const x1 = cx + rOuter * Math.cos(startAngle);
    const y1 = cy + rOuter * Math.sin(startAngle);
    const x2 = cx + rOuter * Math.cos(endAngle);
    const y2 = cy + rOuter * Math.sin(endAngle);
    const x3 = cx + rInner * Math.cos(endAngle);
    const y3 = cy + rInner * Math.sin(endAngle);
    const x4 = cx + rInner * Math.cos(startAngle);
    const y4 = cy + rInner * Math.sin(startAngle);
    const path = [
      `M ${x1} ${y1}`,
      `A ${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4}`,
      'Z',
    ].join(' ');
    return { path, color: colorAt(i), it };
  });

  return (
    <div className="overview-donut-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overview-donut">
        {arcs.map(({ path, color, it }) => (
          <path
            key={it.label}
            d={path}
            fill={color}
            stroke="var(--color-background-elevated, #fff)"
            strokeWidth="1.5"
            className={onSelect ? 'overview-donut-slice clickable' : 'overview-donut-slice'}
            onClick={onSelect ? () => onSelect(it.label) : undefined}
          >
            <title>{`${it.label} — ${fmt(it.count)} (${pct(it.share)})`}</title>
          </path>
        ))}
        <text x={cx} y={cy - 4} textAnchor="middle" className="overview-donut-center-value">
          {fmt(total)}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" className="overview-donut-center-label">
          total
        </text>
      </svg>
      <ul className="overview-donut-legend">
        {items.map((it, i) => (
          <li key={it.label}>
            <button
              type="button"
              className="overview-donut-legend-row"
              onClick={onSelect ? () => onSelect(it.label) : undefined}
              title={`${it.label} — ${fmt(it.count)} · ${it.topicSpread} ${spreadLabel}`}
            >
              <span className="overview-donut-swatch" style={{ background: colorAt(i) }} />
              <span className="overview-donut-legend-label">{it.label}</span>
              <span className="overview-donut-legend-pct">{pct(it.share)}</span>
              <span className="overview-donut-legend-spread">×{it.topicSpread}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Squarified treemap ────────────────────────────────────────────────
function squarify(items, x, y, w, h) {
  const rects = [];
  const total = items.reduce((s, it) => s + it.count, 0) || 1;
  const area = w * h;
  const scaled = items.map((it) => ({ ...it, _area: (it.count / total) * area }));

  const worst = (row, length) => {
    const sum = row.reduce((s, r) => s + r._area, 0);
    const rmax = Math.max(...row.map((r) => r._area));
    const rmin = Math.min(...row.map((r) => r._area));
    const s2 = sum * sum;
    const l2 = length * length;
    return Math.max((l2 * rmax) / s2, s2 / (l2 * rmin));
  };

  let cx = x, cy = y, cw = w, ch = h;
  const layoutRow = (row, length, horizontal) => {
    const sum = row.reduce((s, r) => s + r._area, 0);
    let offset = 0;
    if (horizontal) {
      const rowH = sum / length;
      row.forEach((r) => {
        const rW = r._area / rowH;
        rects.push({ x: cx + offset, y: cy, w: rW, h: rowH, item: r });
        offset += rW;
      });
      cy += rowH;
      ch -= rowH;
    } else {
      const rowW = sum / length;
      row.forEach((r) => {
        const rH = r._area / rowW;
        rects.push({ x: cx, y: cy + offset, w: rowW, h: rH, item: r });
        offset += rH;
      });
      cx += rowW;
      cw -= rowW;
    }
  };

  let remaining = scaled.slice();
  let row = [];
  while (remaining.length > 0) {
    const horizontal = cw >= ch;
    const length = horizontal ? cw : ch;
    const next = remaining[0];
    const newRow = row.concat([next]);
    if (row.length === 0 || worst(newRow, length) <= worst(row, length)) {
      row = newRow;
      remaining = remaining.slice(1);
    } else {
      layoutRow(row, length, horizontal);
      row = [];
    }
  }
  if (row.length > 0) {
    const horizontal = cw >= ch;
    const length = horizontal ? cw : ch;
    layoutRow(row, length, horizontal);
  }
  return rects;
}

function Treemap({ items, onSelect, fmt, pct, recordLabel }) {
  const w = 360, h = 280;
  const sorted = useMemo(() => [...items].sort((a, b) => b.count - a.count), [items]);
  const rects = useMemo(() => squarify(sorted, 0, 0, w, h), [sorted]);

  return (
    <div className="overview-treemap-wrap">
      <svg viewBox={`0 0 ${w} ${h}`} className="overview-treemap">
        {rects.map((r, i) => {
          const showLabel = r.w > 60 && r.h > 28;
          const showValue = r.w > 60 && r.h > 44;
          return (
            <g
              key={r.item.label}
              className={onSelect ? 'overview-treemap-tile clickable' : 'overview-treemap-tile'}
              onClick={onSelect ? () => onSelect(r.item.label) : undefined}
            >
              <title>{`${clipTopicName(r.item.label)} — ${fmt(r.item.count)} ${recordLabel}s (${pct(r.item.share)})`}</title>
              <rect
                x={r.x} y={r.y} width={r.w} height={r.h}
                fill={colorAt(i)}
                stroke="var(--color-background-elevated, #fff)"
                strokeWidth="1.5"
              />
              {showLabel && (
                <foreignObject x={r.x + 4} y={r.y + 4} width={r.w - 8} height={r.h - 8}>
                  <div className="overview-treemap-label" xmlns="http://www.w3.org/1999/xhtml">
                    <div className="overview-treemap-name">{clipTopicName(r.item.label)}</div>
                    {showValue && (
                      <div className="overview-treemap-value">{fmt(r.item.count)} · {pct(r.item.share)}</div>
                    )}
                  </div>
                </foreignObject>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Opportunity scatter ───────────────────────────────────────────────
function OpportunityScatter({ items, onSelect, fmt, pct, recordLabel }) {
  const w = 720, h = 320;
  const pad = { l: 50, r: 20, t: 16, b: 40 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;

  const maxCount = items.reduce((m, it) => Math.max(m, it.count), 0) || 1;
  const xScale = (c) => pad.l + (c / maxCount) * innerW;
  const yScale = (cov) => pad.t + (1 - cov) * innerH;
  const rScale = (c) => 4 + Math.sqrt(c / maxCount) * 14;

  const yTicks = [0, 0.25, 0.5, 0.75, 1];
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * maxCount);

  return (
    <div className="overview-scatter-wrap">
      <svg viewBox={`0 0 ${w} ${h}`} className="overview-scatter" preserveAspectRatio="xMidYMid meet">
        {/* Soft "opportunity zone" — radial gradient anchored at bottom-right.
            Fuzzy edges deliberately avoid implying a hard cutoff. */}
        <defs>
          <radialGradient
            id="ov-opportunity-grad"
            cx={pad.l + innerW}
            cy={pad.t + innerH}
            r={Math.max(innerW, innerH) * 0.85}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" className="overview-scatter-zone-stop-inner" />
            <stop offset="55%" className="overview-scatter-zone-stop-mid" />
            <stop offset="100%" className="overview-scatter-zone-stop-outer" />
          </radialGradient>
        </defs>
        <rect
          x={pad.l} y={pad.t} width={innerW} height={innerH}
          fill="url(#ov-opportunity-grad)"
          className="overview-scatter-zone"
        />
        <text
          x={pad.l + innerW - 10}
          y={pad.t + innerH - 10}
          textAnchor="end"
          className="overview-scatter-quadrant-label"
        >
          opportunity zone
        </text>
        <text
          x={pad.l + innerW - 10}
          y={pad.t + innerH - 24}
          textAnchor="end"
          className="overview-scatter-quadrant-sublabel"
        >
          ← fuzzy, directional
        </text>

        {/* Y gridlines + labels */}
        {yTicks.map((t) => (
          <g key={`y${t}`}>
            <line x1={pad.l} x2={w - pad.r} y1={yScale(t)} y2={yScale(t)} className="overview-scatter-grid" />
            <text x={pad.l - 8} y={yScale(t) + 4} textAnchor="end" className="overview-scatter-axis-label">
              {Math.round(t * 100)}%
            </text>
          </g>
        ))}
        {/* X gridlines + labels */}
        {xTicks.map((t, i) => (
          <g key={`x${i}`}>
            <line x1={xScale(t)} x2={xScale(t)} y1={pad.t} y2={h - pad.b} className="overview-scatter-grid" />
            <text x={xScale(t)} y={h - pad.b + 16} textAnchor="middle" className="overview-scatter-axis-label">
              {fmt(Math.round(t))}
            </text>
          </g>
        ))}
        {/* Axis titles */}
        <text x={pad.l + innerW / 2} y={h - 6} textAnchor="middle" className="overview-scatter-axis-title">
          {recordLabel}s (volume)
        </text>
        <text
          x={-(pad.t + innerH / 2)} y={14}
          textAnchor="middle"
          transform="rotate(-90)"
          className="overview-scatter-axis-title"
        >
          theme coverage
        </text>

        {/* Points */}
        {items.map((it) => (
          <g
            key={it.label}
            className={onSelect ? 'overview-scatter-point clickable' : 'overview-scatter-point'}
            onClick={onSelect ? () => onSelect(it.label) : undefined}
            transform={`translate(${xScale(it.count)}, ${yScale(it.themeCoverage)})`}
          >
            <title>{`${clipTopicName(it.label)} — ${fmt(it.count)} ${recordLabel}s, ${pct(it.themeCoverage)} themed`}</title>
            <circle r={rScale(it.count)} />
            <text y={-rScale(it.count) - 4} textAnchor="middle" className="overview-scatter-point-label">
              {clipTopicName(it.label)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default OverviewPanel;
